import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Link } from "react-router-dom";
import css from "./style.scss";
import BookmarkIcon from "../../../assets/images/Bookmark.svg";

const RecentProperty = ({
  listName,
  count = 0,
  groupId,
  onAdditionalClick,
  isIconHovered,
  history,
  redirectFlag,
  disabled
}) => {
  if (!listName || !groupId) return null;

  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef(null);

  // recompute when name changes, on resize, or when fonts/layout settle
  useEffect(() => {
    const check = () => {
      if (!textRef.current) return;
      setShowTooltip(textRef.current.scrollWidth > textRef.current.clientWidth);
    };
    check();

    // observe size changes of parent (width changes affect ellipsis)
    const parent = textRef.current?.parentElement;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (ro && parent) ro.observe(parent);

    // also react to window resizes and next paint
    window.addEventListener("resize", check);
    const id = setTimeout(check, 0); // after paint

    return () => {
      window.removeEventListener("resize", check);
      clearTimeout(id);
      ro?.disconnect();
    };
  }, [listName]);

  // additionalCount: 1->0 (hide), 2->1, 3->2, ...
  const additionalCount = Math.max(0, (count || 0) - 1);
  const to = `/property/group/${groupId}`;

  const handleNavigation = () => history?.push?.(to);

  const TextEl = (
    <span
      ref={textRef}
      className={css.listText}
      title={showTooltip ? listName : undefined}
    >
      {listName}
    </span>
  );

  return (
    <div
      className={classNames(css.leadlistContainers, { [css["hide-on-hover"]]: isIconHovered })}
      onClick={(e) => e.stopPropagation()}
    >
      {redirectFlag ? (
        <Link to={to} className={css.recentList} tabIndex={0} role="button">
          <img src={BookmarkIcon} alt="Bookmark" className={css.bookmarkIcon} />
          <span className={css.link}>{TextEl}</span>
        </Link>
      ) : (
        <div
          className={css.recentList}
          onClick={handleNavigation}
          role="button"
          tabIndex={0}
        >
          <img src={BookmarkIcon} alt="Bookmark" className={css.bookmarkIcon} />
          <div className={css.link}>{TextEl}</div>
        </div>
      )}

      {additionalCount > 0 && (
        <button
          type="button"
          className={classNames(
            css.additionalCount,
            { [css['disabled-click']]: !disabled } // Conditionally apply the new CSS class
          )}
          onClick={(e) => { e.stopPropagation(); onAdditionalClick?.(); }}
          aria-label={`Show all lists (${count})`}
          style={{ border: "none" }}
          disabled={!disabled}
        >
          +{additionalCount}
        </button>
      )}
    </div>
  );
};

RecentProperty.propTypes = {
  listName: PropTypes.string,
  count: PropTypes.number,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAdditionalClick: PropTypes.func,
  isIconHovered: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  redirectFlag: PropTypes.bool,
  disabled: PropTypes.bool
};

export default RecentProperty;
